import React, { useEffect, useState } from 'react';
import { shopApi } from '../utils/shopApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Tag, Plus, Trash2 } from 'lucide-react';

const EMPTY = { code: '', discount_type: 'percentage', discount_value: 10, max_uses: '', min_order_amount: 0, active: true, expires_at: '' };

export default function PromoCodesPage() {
  const [codes, setCodes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => shopApi.get(`/shop/promo`).then(r => setCodes(r.data)).catch(() => toast.error('Failed to load promo codes'));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, max_uses: form.max_uses ? parseInt(form.max_uses) : null, expires_at: form.expires_at || null, discount_value: parseFloat(form.discount_value), min_order_amount: parseFloat(form.min_order_amount) || 0 };
      await shopApi.post(`/shop/promo`, payload);
      toast.success('Promo code created');
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this promo code?')) return;
    await shopApi.delete(`/shop/promo/${id}`);
    toast.success('Deleted');
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif font-bold flex items-center gap-2"><Tag className="w-7 h-7 text-primary" />Promo Codes</h1>
          <p className="text-sm text-muted-foreground">Discount codes for the online store.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="promo-new-btn"><Plus className="w-4 h-4 mr-1" />New code</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create promo code</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm(f => ({...f, code: e.target.value.toUpperCase()}))} placeholder="e.g. WELCOME10" data-testid="promo-code-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.discount_type} onValueChange={(v) => setForm(f => ({...f, discount_type: v}))}>
                    <SelectTrigger data-testid="promo-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="fixed">Fixed amount (RWF)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Value</Label><Input type="number" value={form.discount_value} onChange={(e) => setForm(f => ({...f, discount_value: e.target.value}))} data-testid="promo-value-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Min order (RWF)</Label><Input type="number" value={form.min_order_amount} onChange={(e) => setForm(f => ({...f, min_order_amount: e.target.value}))} data-testid="promo-min-input" /></div>
                <div><Label>Max uses (optional)</Label><Input type="number" value={form.max_uses} onChange={(e) => setForm(f => ({...f, max_uses: e.target.value}))} data-testid="promo-maxuses-input" /></div>
              </div>
              <div><Label>Expires at (optional)</Label><Input type="datetime-local" value={form.expires_at} onChange={(e) => setForm(f => ({...f, expires_at: e.target.value}))} data-testid="promo-expires-input" /></div>
              <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm(f => ({...f, active: v}))} data-testid="promo-active-switch" /><Label>Active</Label></div>
              <Button onClick={save} className="w-full" data-testid="promo-save-btn">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50"><tr><th className="text-left p-3">Code</th><th className="text-left p-3">Discount</th><th className="text-left p-3">Min order</th><th className="text-left p-3">Uses</th><th className="text-left p-3">Status</th><th></th></tr></thead>
          <tbody>
            {codes.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No promo codes yet.</td></tr>
            ) : codes.map(c => (
              <tr key={c.id} className="border-t border-border" data-testid={`promo-row-${c.id}`}>
                <td className="p-3 font-mono font-semibold">{c.code}</td>
                <td className="p-3">{c.discount_type === 'percentage' ? `${c.discount_value}%` : `RWF ${c.discount_value}`}</td>
                <td className="p-3 text-muted-foreground">{c.min_order_amount ? `RWF ${c.min_order_amount}` : '–'}</td>
                <td className="p-3 text-muted-foreground">{c.uses ?? 0}{c.max_uses ? ` / ${c.max_uses}` : ''}</td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs ${c.active ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'}`}>{c.active ? 'Active' : 'Inactive'}</span></td>
                <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={() => remove(c.id)} data-testid={`promo-delete-${c.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
